import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import colors from "../../Constants/colors";
import {
  useKnowledge,
  useSingleKnowledge,
  useUpdateKnowledge,
} from "../../Query/useKnowledge";
import FileUploader from "../FileUpload/FileUploader";
import FileUploaderAction from "../FileUpload/FileUploaderAction";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { allowedExtensionsKnowledge } from "../../Constants/fileExtensions";
import { ComoInput } from "../Common/ComoInput";
import { ComoButton } from "../Common/ComoButton";
import { ComoSwitch } from "../Common/ComoSwitch";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { hasRole } from "../../Helpers/authHelpers";
import { UserRole } from "../../Enums/companyStatus";
import { Pipeline } from "../../Enums/pipeline";

const KnowledgeBase = () => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [pipeline, setPipeline] = useState<Pipeline>(Pipeline.LEGACY);
  const isAdmin = hasRole(UserRole.ADMIN);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { knowledgeBaseId } = useParams<{ knowledgeBaseId?: string }>();
  const location = useLocation();
  const { t } = useTranslation("createKnowledgeBase");
  const { colors: workspace } = useWorkspaceColors();

  const { mutate: createMutate } = useKnowledge();
  const {
    data: singleData,
    isLoading: isFetching,
    isError: isFetchingError,
    error: fetchingError,
  } = useSingleKnowledge(Number(knowledgeBaseId));
  const { mutate: updateMutate } = useUpdateKnowledge();

  if (isFetchingError && fetchingError.response?.status === 404) {
    navigate("/knowledge_base/");
  }

  useEffect(() => {
    if (knowledgeBaseId) {
      setMode("edit");
    } else {
      setMode("create");
    }
    setName("");
    setDescription("");
    setFiles([]);
  }, [knowledgeBaseId]);

  useEffect(() => {
    if (mode === "edit" && singleData) {
      setName(singleData.name);
      setDescription(singleData.description);
    }
  }, [mode, singleData]);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (mode === "create") {
      createMutate(
        { name, description, files, pipeline },
        {
          onSuccess: () => {
            navigate("/knowledge_base/");
            setIsSubmitting(false);
          },
          onError: () => {
            setIsSubmitting(false);
          },
        },
      );
    } else {
      updateMutate(
        { knowledgeBaseId: Number(knowledgeBaseId), name, description },
        {
          onSuccess: () => {
            navigate("/knowledge_base/");
            setIsSubmitting(false);
          },
          onError: () => {
            setIsSubmitting(false);
          },
        },
      );
    }
  };

  const handleRemoveFile = (fileName: string) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
  };

  const handleOpenFile = (file: File) => {
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  };

  const clearAllFiles = () => {
    setFiles([]);
    setPage(0);
  };

  const sectionTitleSx = {
    color: workspace.primary_dark,
    fontWeight: 600,
    fontSize: "0.85rem",
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    borderBottom: `1px solid ${colors.gray200}`,
    pb: 0.5,
    mb: 2,
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: colors.gray50 }}>
      <Box sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton
              onClick={() => navigate("/knowledge_base/")}
              size="small"
              sx={{ color: workspace.primary_dark }}
            >
              <ArrowBackIcon />
            </IconButton>
            <StorageOutlinedIcon
              sx={{ fontSize: 32, color: workspace.primary_dark }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: workspace.primary_dark }}
            >
              {mode === "create"
                ? t("createKnowledgeBase.createTitle")
                : t("createKnowledgeBase.editTitle")}
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          {isFetching && !singleData ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: workspace.accent }} />
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              key={location.pathname}
            >
              <Typography sx={sectionTitleSx}>
                {t("createKnowledgeBase.details")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <ComoInput
                    label={t("createKnowledgeBase.name")}
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={isSubmitting}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <ComoInput
                    label={t("createKnowledgeBase.description")}
                    name="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    multiline
                    disabled={isSubmitting}
                    rows={4}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              {mode === "create" && (
                <Box sx={{ mt: 4 }}>
                  <Typography sx={sectionTitleSx}>
                    {t("createKnowledgeBase.files")}
                  </Typography>
                  <FileUploader
                    uploadedFiles={files}
                    setUploadedFiles={setFiles}
                    allowedExtensions={allowedExtensionsKnowledge}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleRemoveFile={handleRemoveFile}
                    handleOpenFile={handleOpenFile}
                    disabled={isSubmitting}
                  />

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mt: 1 }}
                  >
                    <FileUploaderAction
                      buttonTitle={t("createKnowledgeBase.clear")}
                      tooltipTitle={t(
                        "createKnowledgeBase.clearTooltip_noFiles",
                      )}
                      disableHoverListener={files.length > 0}
                      disabled={isSubmitting || files.length === 0}
                      onClick={clearAllFiles}
                    />
                    <TablePagination
                      component="div"
                      count={files.length}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[]}
                      disabled={isSubmitting}
                      labelDisplayedRows={({ from, to, count }) =>
                        t("createKnowledgeBase.showing", { from, to, count })
                      }
                      sx={{
                        "& .MuiTablePagination-displayedRows": {
                          color: colors.gray600,
                          fontSize: "0.8rem",
                        },
                        "& .MuiIconButton-root": {
                          color: workspace.primary_dark,
                        },
                      }}
                    />
                  </Stack>

                  {isAdmin && (
                    <FormControlLabel
                      control={
                        <ComoSwitch
                          checked={pipeline === Pipeline.MISTRAL}
                          onChange={(e) =>
                            setPipeline(
                              e.target.checked
                                ? Pipeline.MISTRAL
                                : Pipeline.LEGACY,
                            )
                          }
                          disabled={isSubmitting}
                        />
                      }
                      label={t("createKnowledgeBase.useMistralPipeline")}
                      sx={{
                        mt: 1,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.8rem",
                          color: colors.gray500,
                        },
                      }}
                    />
                  )}
                </Box>
              )}

              <Box sx={{ mt: 4 }}>
                <ComoButton
                  type="submit"
                  disabled={isSubmitting || !name.trim() || !description.trim()}
                  fullWidth
                  sx={{ py: 1.25, fontWeight: 600 }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={20} />
                  ) : mode === "create" ? (
                    t("createKnowledgeBase.submit")
                  ) : (
                    t("createKnowledgeBase.update")
                  )}
                </ComoButton>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default KnowledgeBase;
